import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Alert,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { DWLFChart } from '@andywilliams/dwlf-charting';
import { loadMarketData } from '../features/markets/marketsSlice';
import { USER_SETTING_CHART_PREFERENCES, USER_SETTING_SEMI_LIVE_CANDLES } from '../features/userSettings/userSettingsSlice';
import {
  getAxisColorScheme,
  normalizeCrosshairSnapMode,
  normalizeCrosshairPriceLabelPreference,
  normalizeAdaptiveGridPreference,
} from '../constants/chartPreferences';
import DWLFLoader from './DWLFLoader';
import { isAbsent, absentColor, formatCurrency as sharedFormatCurrency, formatPercentage as sharedFormatPercentage } from '../utils/metricFormat';
import useIsDark from '../hooks/useIsDark';
import useSchemeColors from '../hooks/useSchemeColors';
import { candleStyle } from './chart/candleStyle';

/**
 * TradeChartModal - Modal component to visualize individual trades on a chart
 * Shows market data context around the trade period with entry/exit markers
 */
// `derivePnl`: estimate P&L from prices when the trade has none (live signals). Off for backtest
// trades, whose figures come from the API or show as absent (charter invariant 8).
const TradeChartModal = ({ open, onClose, trade, showRiskTargets = false, derivePnl = true }) => {
  const dispatch = useDispatch();
  const isDark = useIsDark();
  const scheme = useSchemeColors();
  // The chart is SVG with fill attributes, so it takes resolved Signal colours (not CSS variables).
  const chartPalette = useMemo(() => {
    const surface = { background: scheme.bg, grid: scheme.ink(0.06), text: scheme.ink(0.7), tooltipBackground: scheme.elevated };
    return { dark: surface, light: surface };
  }, [scheme]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get market data from Redux store
  const marketDataBySymbol = useSelector((state) => state.markets.marketData || {});
  const marketData = trade?.symbol ? marketDataBySymbol[trade.symbol] : null;

  const userSettings = useSelector((state) => state.userSettings.settings || {});

  // Memoize chart preference objects to avoid creating new references on every render
  const chartPreferencesStore = useMemo(() => {
    const raw = userSettings?.[USER_SETTING_CHART_PREFERENCES];
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [userSettings]);

  const semiLiveCandlesEnabled = userSettings?.[USER_SETTING_SEMI_LIVE_CANDLES] !== false;

  const globalChartPreferences = useMemo(() => {
    const raw = chartPreferencesStore?.__global;
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [chartPreferencesStore]);

  const axisColorSchemeId = globalChartPreferences.axisColorScheme;
  const axisColors = useMemo(
    () => getAxisColorScheme(axisColorSchemeId).colors,
    [axisColorSchemeId],
  );
  const crosshairSnapMode = useMemo(
    () => normalizeCrosshairSnapMode(globalChartPreferences?.crosshairSnapMode),
    [globalChartPreferences],
  );
  const showCrosshairPriceLabel = useMemo(
    () => normalizeCrosshairPriceLabelPreference(globalChartPreferences?.showCrosshairPriceLabel),
    [globalChartPreferences],
  );
  const showAdaptiveGrid = useMemo(
    () => normalizeAdaptiveGridPreference(globalChartPreferences?.showAdaptiveGrid),
    [globalChartPreferences],
  );
  const chartAppearanceKey = useMemo(() => {
    const paletteKey = axisColors && typeof axisColors === 'object'
      ? `${axisColors.dark || ''}|${axisColors.light || ''}`
      : 'default';
    return [
      paletteKey,
      crosshairSnapMode || 'series',
      showCrosshairPriceLabel ? 'label:on' : 'label:off',
      showAdaptiveGrid ? 'grid:on' : 'grid:off',
    ].join('::');
  }, [axisColors?.dark, axisColors?.light, crosshairSnapMode, showCrosshairPriceLabel, showAdaptiveGrid]);

  // Format currency helper
  // Absent renders as ABSENT, never as $0.00 — a fabricated zero price or P&L is
  // indistinguishable from a real one. See src/utils/metricFormat.js.
  const formatCurrency = useCallback((amount) => sharedFormatCurrency(amount), []);

  // Format percentage helper. Previously returned the literal '0.00%' for absent
  // values — the last of three rival implementations that did so.
  const formatPercentage = useCallback((value) => sharedFormatPercentage(value), []);

  // Format date helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
      return 'N/A';
    }
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const toTimestamp = useCallback((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (!value) {
      return null;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const getContextCandleBuffer = useCallback((durationCandles) => {
    const scaled = Math.round(durationCandles * 0.75);
    return Math.min(150, Math.max(30, scaled));
  }, []);

  // Filter market data to a candle window centered on the trade
  const chartData = useMemo(() => {
    if (!marketData?.candles) {
      return [];
    }

    const mapped = marketData.candles
      .map((candle) => {
        const timestamp = toTimestamp(
          candle?.t
            ?? candle?.timestamp
            ?? candle?.time
            ?? candle?.date
            ?? candle?.datetime
            ?? candle?.weekStartDate,
        );

        if (!Number.isFinite(timestamp)) {
          return null;
        }

        const isoDate = candle?.date ?? new Date(timestamp).toISOString();

        return {
          ...candle,
          date: isoDate,
          timestamp,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

    if (!mapped.length || !trade?.entryDate) {
      return mapped;
    }

    const entryTime = toTimestamp(trade.entryDate);
    const exitReference = trade.exitDate || trade.currentDate || trade.entryDate;
    const exitTime = toTimestamp(exitReference);
    if (!Number.isFinite(entryTime) || !Number.isFinite(exitTime)) {
      return mapped;
    }

    const entryIndex = mapped.findIndex((candle) => candle.timestamp >= entryTime);
    const exitIndex = mapped.findIndex((candle) => candle.timestamp >= exitTime);
    const resolvedEntryIndex = entryIndex >= 0 ? entryIndex : 0;
    const resolvedExitIndex = exitIndex >= 0 ? exitIndex : mapped.length - 1;
    const durationCandles = Math.max(1, resolvedExitIndex - resolvedEntryIndex + 1);
    const buffer = getContextCandleBuffer(durationCandles);

    const startIndex = Math.max(0, resolvedEntryIndex - buffer);
    const endIndex = Math.min(mapped.length - 1, resolvedExitIndex + buffer);

    return mapped.slice(startIndex, endIndex + 1);
  }, [marketData, toTimestamp, trade, getContextCandleBuffer]);

  const priceCandles = useMemo(() => {
    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    return chartData
      .map((candle) => {
        const timestamp = Number(candle?.timestamp);
        const open = toNumber(candle?.open ?? candle?.o ?? candle?.Open ?? candle?.openPrice ?? candle?.open_price);
        const high = toNumber(candle?.high ?? candle?.h ?? candle?.High ?? candle?.highPrice ?? candle?.high_price);
        const low = toNumber(candle?.low ?? candle?.l ?? candle?.Low ?? candle?.lowPrice ?? candle?.low_price);
        const close = toNumber(candle?.close ?? candle?.c ?? candle?.Close ?? candle?.closePrice ?? candle?.close_price);

        if (!Number.isFinite(timestamp) || open === null || high === null || low === null || close === null) {
          return null;
        }

        return {
          t: timestamp,
          o: open,
          h: high,
          l: low,
          c: close,
        };
      })
      .filter(Boolean);
  }, [chartData]);

  const lastChartDate = chartData.length > 0 ? chartData[chartData.length - 1].date : null;
  const hasExit = Boolean(trade?.exitDate && Number.isFinite(trade?.exitPrice));
  const currentPrice = Number.isFinite(trade?.currentPrice) ? trade.currentPrice : null;
  const effectiveExitDate = hasExit
    ? trade.exitDate
    : trade?.currentDate || lastChartDate || trade?.entryDate;
  const effectiveExitPrice = hasExit
    ? trade.exitPrice
    : currentPrice ?? trade?.exitPrice ?? trade?.entryPrice;

  // Use full-position P&L when possible (price diff * quantity).
  const quantity = Number.isFinite(trade?.quantity) && trade.quantity > 0
    ? trade.quantity
    : 1;

  const effectivePnl = Number.isFinite(trade?.pnl)
    ? trade.pnl
    : !derivePnl ? null : (Number.isFinite(trade?.entryPrice) && Number.isFinite(effectiveExitPrice)
        ? (effectiveExitPrice - trade.entryPrice) * quantity
        : 0);
  const effectiveReturnPercent = Number.isFinite(trade?.returnPercent)
    ? trade.returnPercent
    : !derivePnl ? null : (Number.isFinite(trade?.entryPrice) && Number.isFinite(effectiveExitPrice) && trade.entryPrice !== 0
        ? (effectiveExitPrice - trade.entryPrice) / trade.entryPrice
        : 0);
  // An absent P&L is not a profit. `(null || 0) >= 0` was TRUE, so an unknown
  // result was scored — and coloured — as a winning trade.
  const pnlAbsent = isAbsent(effectivePnl);
  const isProfit = !pnlAbsent && effectivePnl >= 0;
  // Exit price, marker and guide: neutral when the result is absent — not a win, not a loss either.
  const exitColor = pnlAbsent ? '#9E9E9E' : isProfit ? '#4CAF50' : '#F44336';
  const exitLabel = hasExit ? 'Exit' : 'Current';
  const exitPriceDisplay = Number.isFinite(effectiveExitPrice) ? effectiveExitPrice : trade?.entryPrice;
  const displayDuration = Number.isFinite(trade?.duration)
    ? trade.duration
    : (trade?.entryDate && effectiveExitDate
        ? Math.max(
            0,
            Math.round(
              (new Date(effectiveExitDate).getTime() - new Date(trade.entryDate).getTime()) /
                (1000 * 60 * 60 * 24)
            )
          )
        : 0);
  const triggeredEvents = trade?.metadata?.triggeredEvents || trade?.triggeredEvents || [];

  // Helper to format exit reason for display
  const formatExitReason = useCallback((reason) => {
    const reasonMap = {
      'stop_loss': 'Stop Loss',
      'trailing_stop': 'Trailing Stop',
      'take_profit': 'Take Profit',
      'signal_exit': 'Exit Signal',
      'target2R': '2R Target',
      'target3R': '3R Target',
      'target5R': '5R Target',
      'target10R': '10R Target',
      'backtest_end': 'Backtest End',
      'entry_not_found': 'Entry Not Found'
    };
    return reasonMap[reason] || reason || 'Unknown';
  }, []);

  // Helper to get color for exit reason
  const getExitReasonColor = useCallback((reason) => {
    const colorMap = {
      'stop_loss': '#d32f2f',
      'trailing_stop': '#f57c00',
      'take_profit': '#388e3c',
      'signal_exit': '#7b1fa2',
      'target2R': '#388e3c',
      'target3R': '#2e7d32',
      'target5R': '#1b5e20',
      'target10R': '#004d40',
      'backtest_end': '#616161',
      'entry_not_found': '#757575'
    };
    return colorMap[reason] || '#555';
  }, []);

  // Helper to extract event types from exit conditions
  const getExitConditionEvents = useCallback((exitConditions) => {
    if (!exitConditions) return [];
    
    // Handle string (JSON) format
    let conditions = exitConditions;
    if (typeof exitConditions === 'string') {
      try {
        conditions = JSON.parse(exitConditions);
      } catch {
        return [];
      }
    }

    const events = [];
    
    const extractEvents = (cond) => {
      if (!cond) return;
      
      if (cond.event) {
        events.push(cond.event);
      }
      
      // Handle nested AND/OR structures
      if (cond.and) {
        const andConditions = Array.isArray(cond.and) ? cond.and : cond.and.conditions;
        if (andConditions) {
          andConditions.forEach(extractEvents);
        }
      }
      
      if (cond.or) {
        const orConditions = Array.isArray(cond.or) ? cond.or : cond.or.conditions;
        if (orConditions) {
          orConditions.forEach(extractEvents);
        }
      }
      
      if (cond.sequence?.steps) {
        cond.sequence.steps.forEach(extractEvents);
      }
    };
    
    extractEvents(conditions);
    return events;
  }, []);

  // Helper to format event names for display
  const formatEventName = useCallback((eventName) => {
    const nameMap = {
      'lower_high': 'Lower High',
      'swing_lower_high': 'Lower High',
      'lower_low': 'Lower Low',
      'swing_lower_low': 'Lower Low',
      'trendline_breach_bearish': 'Trendline Breach ↓',
      'trendline_break_bearish': 'Trendline Break ↓',
      'trendline_breach_bullish': 'Trendline Breach ↑',
      'trendline_break_bullish': 'Trendline Break ↑',
      'price_below_ema': 'Below EMA',
      'closes_below_ema_10': 'Below EMA(10)',
      'closes_below_ema_20': 'Below EMA(20)',
      'volume_spike': 'Volume Spike'
    };
    return nameMap[eventName] || eventName?.replace(/_/g, ' ') || 'Unknown';
  }, []);

  const entryTimestamp = useMemo(() => toTimestamp(trade?.entryDate), [trade?.entryDate, toTimestamp]);

  const exitTimestamp = useMemo(
    () => (effectiveExitDate ? toTimestamp(effectiveExitDate) : null),
    [effectiveExitDate, toTimestamp],
  );

  const pricePaneGuides = useMemo(() => {
    const guides = [];

    if (Number.isFinite(trade?.entryPrice)) {
      guides.push({
        y: trade.entryPrice,
        dashed: true,
        color: '#2196F3',
        label: `Entry ${formatCurrency(trade.entryPrice)}`,
      });
    }

    if (Number.isFinite(exitPriceDisplay)) {
      guides.push({
        y: exitPriceDisplay,
        dashed: true,
        color: exitColor,
        label: `${exitLabel}: ${formatCurrency(exitPriceDisplay)}`,
      });
    }

    if (Number.isFinite(trade?.stopLoss)) {
      guides.push({
        y: trade.stopLoss,
        dashed: true,
        color: '#FF9800',
        label: `${Number.isFinite(trade?.trailingStopLevel) ? 'Initial stop' : 'Stop'} ${formatCurrency(trade.stopLoss)}`,
      });
    }

    // A trailing stop's level now (DWLF-341), beside the initial stop R is measured from.
    if (Number.isFinite(trade?.trailingStopLevel)) {
      guides.push({
        y: trade.trailingStopLevel,
        dashed: true,
        color: '#f57c00',
        label: `Trailing stop ${formatCurrency(trade.trailingStopLevel)}`,
      });
    }

    if (
      showRiskTargets
      && Number.isFinite(trade?.entryPrice)
      && Number.isFinite(trade?.stopLoss)
      && trade.entryPrice !== trade.stopLoss
    ) {
      const risk = trade.entryPrice - trade.stopLoss;
      const isLong = risk > 0;
      const absRisk = Math.abs(risk);
      [3, 5, 10].forEach((multiple) => {
        const level = isLong
          ? trade.entryPrice + absRisk * multiple
          : trade.entryPrice - absRisk * multiple;
        guides.push({
          y: level,
          dashed: true,
          color: 'rgba(102, 187, 106, 0.6)',
          label: `${multiple}R`,
        });
      });
    }

    return guides;
  }, [trade, exitPriceDisplay, exitColor, exitLabel, showRiskTargets, formatCurrency]);

  const tradeMarkerSeries = useMemo(() => {
    const series = [];

    if (Number.isFinite(trade?.entryPrice) && Number.isFinite(entryTimestamp)) {
      series.push({
        key: 'Trade Entry Marker',
        type: 'marker',
        data: [{
          t: entryTimestamp,
          v: trade.entryPrice,
          label: 'ENTRY',
        }],
        style: {
          color: '#2196F3',
          markerShape: 'circle',
          markerSize: 7,
          markerOffsetY: -12,
          markerTextColor: '#2196F3',
          markerTextOffsetY: -6,
        },
      });
    }

    if (Number.isFinite(exitPriceDisplay) && Number.isFinite(exitTimestamp)) {
      series.push({
        key: 'Trade Exit Marker',
        type: 'marker',
        data: [{
          t: exitTimestamp,
          v: exitPriceDisplay,
          label: exitLabel.toUpperCase(),
        }],
        style: {
          color: exitColor,
          markerShape: 'circle',
          markerSize: 7,
          markerOffsetY: -12,
          markerTextColor: exitColor,
          markerTextOffsetY: -6,
        },
      });
    }

    return series;
  }, [trade, entryTimestamp, exitTimestamp, exitPriceDisplay, exitLabel, exitColor]);

  const tradePositionSeries = useMemo(() => {
    if (!Number.isFinite(trade?.entryPrice) || !Number.isFinite(entryTimestamp)) {
      return [];
    }

    const stop = Number.isFinite(trade?.stopLoss) ? trade.stopLoss : trade.entryPrice;
    const target = Number.isFinite(exitPriceDisplay) ? exitPriceDisplay : trade.entryPrice;
    const messages = [];

    if (Number.isFinite(entryTimestamp)) {
      messages.push({
        t: entryTimestamp,
        v: trade.entryPrice,
        text: `${trade.symbol ?? ''}\nP&L: ${formatCurrency(effectivePnl)}${hasExit ? '' : ' (Unrealized)'}\nDuration: ${displayDuration} days`,
      });
    }

    const rewardFill = isProfit
      ? 'rgba(76, 175, 80, 0.28)'
      : 'rgba(148, 163, 184, 0.22)';

    return [{
      key: 'Trade Zone',
      type: 'position',
      data: [{
        start: entryTimestamp,
        end: Number.isFinite(exitTimestamp) ? exitTimestamp : undefined,
        entry: trade.entryPrice,
        stop,
        target,
        messages,
        riskColor: 'rgba(244, 67, 54, 0.3)',
        rewardColor: rewardFill,
        bubbleColor: 'rgba(0,0,0,0.85)',
        textColor: 'white',
        fontSize: 11,
        pointer: true,
        padding: 6,
      }],
    }];
  }, [
    trade,
    entryTimestamp,
    exitTimestamp,
    exitPriceDisplay,
    isProfit,
    formatCurrency,
    effectivePnl,
    hasExit,
    displayDuration,
  ]);

  const pricePaneSeries = useMemo(() => {
    if (!priceCandles.length) {
      return [];
    }

    const series = [{
      key: 'Price',
      type: 'ohlc',
      data: priceCandles,
      style: candleStyle(scheme),
    }];

    if (tradePositionSeries.length) {
      series.push(...tradePositionSeries);
    }

    if (tradeMarkerSeries.length) {
      series.push(...tradeMarkerSeries);
    }

    return series;
  }, [priceCandles, tradePositionSeries, tradeMarkerSeries, scheme]);

  const timeFormatter = useCallback(
    (timestamp) => new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    [],
  );

  // Calculate optimal y-scale range to show the full trade with buffer
  const yScaleRange = useMemo(() => {
    const pricesToInclude = [];
    
    // Include candle highs and lows
    priceCandles.forEach((candle) => {
      if (Number.isFinite(candle.h)) pricesToInclude.push(candle.h);
      if (Number.isFinite(candle.l)) pricesToInclude.push(candle.l);
    });
    
    // Include trade entry price
    if (Number.isFinite(trade?.entryPrice)) {
      pricesToInclude.push(trade.entryPrice);
    }
    
    // Include exit price
    if (Number.isFinite(exitPriceDisplay)) {
      pricesToInclude.push(exitPriceDisplay);
    }
    
    // Include stop loss
    if (Number.isFinite(trade?.stopLoss)) {
      pricesToInclude.push(trade.stopLoss);
    }
    if (Number.isFinite(trade?.trailingStopLevel)) {
      pricesToInclude.push(trade.trailingStopLevel);
    }
    
    // Include take profit if available
    if (Number.isFinite(trade?.takeProfit)) {
      pricesToInclude.push(trade.takeProfit);
    }
    
    // Include risk targets if showing them
    if (
      showRiskTargets
      && Number.isFinite(trade?.entryPrice)
      && Number.isFinite(trade?.stopLoss)
      && trade.entryPrice !== trade.stopLoss
    ) {
      const risk = trade.entryPrice - trade.stopLoss;
      const isLong = risk > 0;
      const absRisk = Math.abs(risk);
      [3, 5, 10].forEach((multiple) => {
        const level = isLong
          ? trade.entryPrice + absRisk * multiple
          : trade.entryPrice - absRisk * multiple;
        pricesToInclude.push(level);
      });
    }
    
    if (pricesToInclude.length === 0) {
      return null; // Use auto mode
    }
    
    const minPrice = Math.min(...pricesToInclude);
    const maxPrice = Math.max(...pricesToInclude);
    const priceRange = maxPrice - minPrice;
    
    // Add 8% buffer at top and bottom to ensure nothing is cut off
    const buffer = priceRange * 0.08;
    
    return {
      min: minPrice - buffer,
      max: maxPrice + buffer,
    };
  }, [priceCandles, trade, exitPriceDisplay, showRiskTargets]);

  const chartSpec = useMemo(() => {
    if (!pricePaneSeries.length) {
      return { panes: [] };
    }

    // Use fixed scale with calculated range, or fall back to auto
    const yScale = yScaleRange 
      ? { mode: 'fixed', min: yScaleRange.min, max: yScaleRange.max }
      : { mode: 'auto' };

    return {
      panes: [{
        id: 'price',
        title: trade?.symbol ? `${trade.symbol} Trade` : 'Trade',
        heightRatio: 1,
        yScale,
        series: pricePaneSeries,
        guides: pricePaneGuides,
      }],
      timeFormatter,
    };
  }, [pricePaneSeries, pricePaneGuides, timeFormatter, trade, yScaleRange]);

  // Fetch market data when modal opens
  useEffect(() => {
    if (open && trade?.symbol && !marketData) {
      setLoading(true);
      setError(null);
      
      dispatch(loadMarketData({ symbol: trade.symbol, semiLive: semiLiveCandlesEnabled }))
        .unwrap()
        .then(() => {
          setLoading(false);
        })
        .catch((err) => {
          // Handle error which could be a string, object with error/message, or other format
          const errorMessage = typeof err === 'string' 
            ? err 
            : err?.error || err?.message || 'Failed to load market data';
          setError(errorMessage);
          setLoading(false);
        });
    }
  }, [open, trade?.symbol, marketData, dispatch, semiLiveCandlesEnabled]);

  if (!trade) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          bgcolor: 'var(--dwlf-surface)',
          color: 'var(--dwlf-text)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'var(--dwlf-elevated)',
        borderBottom: '1px solid var(--dwlf-ink-12)'
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <ShowChartIcon />
          <Typography variant="h6">
            Trade Analysis - {trade.symbol}
          </Typography>
          <Chip
            icon={pnlAbsent ? undefined : isProfit ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={formatCurrency(effectivePnl)}
            color={pnlAbsent ? 'default' : isProfit ? 'success' : 'error'}
            size="small"
          />
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'var(--dwlf-text)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: 'var(--dwlf-surface)', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="400px"
                flexDirection="column"
                gap={2}
              >
                <DWLFLoader size="medium" />
                <Typography color="text.secondary">
                  Loading market data for {trade.symbol}...
                </Typography>
              </Box>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box p={3}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Box>
            </motion.div>
          )}

          {!loading && !error && priceCandles.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {/* Trade Summary Panel */}
              <Box sx={{ p: 2, bgcolor: 'var(--dwlf-elevated)', borderBottom: '1px solid var(--dwlf-ink-12)' }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Entry
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(trade.entryDate)}
                    </Typography>
                    <Typography variant="h6" color="#2196F3">
                      {formatCurrency(trade.entryPrice)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {exitLabel}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(effectiveExitDate)}
                    </Typography>
                    <Typography variant="h6" color={exitColor}>
                      {formatCurrency(exitPriceDisplay)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      P&L
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color={pnlAbsent ? absentColor : isProfit ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {formatCurrency(effectivePnl)}
                    </Typography>
                    <Typography variant="body2" color={isAbsent(effectiveReturnPercent) ? absentColor : isProfit ? 'success.main' : 'error.main'}>
                      {formatPercentage(effectiveReturnPercent)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Duration
                    </Typography>
                    <Typography variant="h6">
                      {displayDuration} days
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Quantity
                    </Typography>
                    <Typography variant="h6">
                      {trade.quantity || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Triggering events
                  </Typography>
                  {triggeredEvents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Not provided
                    </Typography>
                  ) : (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {triggeredEvents.map((e, idx) => (
                        <Chip
                          key={`${e.eventType || 'event'}-${e.date || 'no-date'}-${idx}`}
                          label={`${e.eventType || 'event'}${e.timeframe ? ` @ ${e.timeframe}` : ''}${e.date ? ` • ${formatDate(e.date)}` : ''}`}
                          size="small"
                          sx={{ bgcolor: 'var(--dwlf-elevated)', color: 'var(--dwlf-text)' }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Exit Information */}
                {hasExit && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Exit trigger
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                      <Chip
                        label={formatExitReason(trade?.exitReason)}
                        size="small"
                        sx={{ 
                          bgcolor: getExitReasonColor(trade?.exitReason),
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                      />
                      {/* Show actual exit triggered events with dates (like entry events) */}
                      {trade?.exitTriggeredEvents && trade.exitTriggeredEvents.length > 0 ? (
                        trade.exitTriggeredEvents.map((e, idx) => (
                          <Chip
                            key={`exit-event-${e.eventType || 'event'}-${e.date || 'no-date'}-${idx}`}
                            label={`${formatEventName(e.eventType || 'event')}${e.timeframe ? ` @ ${e.timeframe}` : ''}${e.date ? ` • ${formatDate(e.date)}` : ''}`}
                            size="small"
                            sx={{ bgcolor: '#5c4d7a', color: '#fff' }}
                          />
                        ))
                      ) : (
                        /* Fallback: show defined exit conditions if no triggered events */
                        trade?.exitReason === 'signal_exit' && trade?.exitConditions && (
                          getExitConditionEvents(trade.exitConditions).map((event, idx) => (
                            <Chip
                              key={`exit-cond-${event}-${idx}`}
                              label={formatEventName(event)}
                              size="small"
                              sx={{ bgcolor: '#5c4d7a', color: '#fff', opacity: 0.7 }}
                            />
                          ))
                        )
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Chart */}
              <Box sx={{ flexGrow: 1, minHeight: '450px', height: '50vh', position: 'relative' }}>
                <DWLFChart
                  key={chartAppearanceKey}
                  spec={chartSpec}
                  darkMode={isDark}
                  palette={chartPalette}
                  showGrid={showAdaptiveGrid}
                  enablePanZoom
                  timeframe={marketData?.timeframe ?? 'daily'}
                  initialVisibleCount={priceCandles.length}
                  extraRightSlots={24}
                  axisColors={axisColors}
                  crosshairSnapMode={crosshairSnapMode}
                  showCrosshairPriceLabel={showCrosshairPriceLabel}
                  // No per-pane hover boxes: the OSS stylesheet that positions them isn't loaded, so they'd fall
                  // under the chart. The hovered date shows on the time axis; the panel above summarises the trade.
                  showPaneTooltips={false}
                />
              </Box>
            </motion.div>
          )}

          {!loading && !error && priceCandles.length === 0 && marketData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Box p={3}>
                <Alert severity="info">
                  No market data available for the selected date range.
                </Alert>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

TradeChartModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trade: PropTypes.shape({
    symbol: PropTypes.string.isRequired,
    entryDate: PropTypes.string.isRequired,
    exitDate: PropTypes.string,
    entryPrice: PropTypes.number.isRequired,
    exitPrice: PropTypes.number,
    pnl: PropTypes.number,
    returnPercent: PropTypes.number,
    duration: PropTypes.number,
    quantity: PropTypes.number,
    stopLoss: PropTypes.number,
    isOpen: PropTypes.bool,
    currentPrice: PropTypes.number,
    currentDate: PropTypes.string
  }),
  showRiskTargets: PropTypes.bool,
  derivePnl: PropTypes.bool
};

export default TradeChartModal;
